/**
 * CodeMirror 6 plugin für Obsidian-Style Wikilinks `[[Title]]`.
 *
 * Features:
 * - Visuelle Hervorhebung von `[[...]]` mit eigener CSS-Klasse
 * - Autocomplete: wenn `[[` getippt wird, async-Lookup gegen /api/notes?q=
 * - Cmd+Click (Ctrl+Click) auf einen Wikilink öffnet die Note (via onNavigate-Callback)
 */
import { type Completion, type CompletionContext, autocompletion } from "@codemirror/autocomplete";
import { type Extension, RangeSetBuilder } from "@codemirror/state";
import {
  Decoration,
  type DecorationSet,
  EditorView,
  ViewPlugin,
  type ViewUpdate,
} from "@codemirror/view";

import { api } from "../../lib/api";

const WIKILINK_RE = /\[\[([^\]|#]+)(?:#([^\]|]+))?(?:\|([^\]]+))?\]\]/g;

const wikilinkDeco = Decoration.mark({ class: "cm-wikilink" });

function buildDecorations(view: EditorView): DecorationSet {
  const builder = new RangeSetBuilder<Decoration>();
  for (const { from, to } of view.visibleRanges) {
    const text = view.state.doc.sliceString(from, to);
    for (const m of text.matchAll(WIKILINK_RE)) {
      const start = from + (m.index ?? 0);
      const end = start + m[0].length;
      builder.add(start, end, wikilinkDeco);
    }
  }
  return builder.finish();
}

const wikilinkDecorationPlugin = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;
    constructor(view: EditorView) {
      this.decorations = buildDecorations(view);
    }
    update(u: ViewUpdate) {
      if (u.docChanged || u.viewportChanged) {
        this.decorations = buildDecorations(u.view);
      }
    }
  },
  { decorations: (v) => v.decorations },
);

function wikilinkCompletion(): Extension {
  return autocompletion({
    override: [
      async (ctx: CompletionContext) => {
        // Match Cursor innerhalb eines unvollständigen `[[...]`
        const before = ctx.matchBefore(/\[\[([^\]\n]*)$/);
        if (!before) return null;
        const query = before.text.slice(2);
        if (query.length < 1 && !ctx.explicit) return null;

        let notes;
        try {
          notes = await api.listNotes({ q: query, limit: 10 });
        } catch {
          return null;
        }

        const options: Completion[] = notes.map((n) => ({
          label: n.title,
          type: "text",
          detail: n.type,
          apply: `${n.title}]]`,
        }));

        return {
          from: before.from + 2,
          options,
          validFor: /^[^\]\n]*$/,
        };
      },
    ],
  });
}

function findWikilinkAtPos(text: string, pos: number): { target: string; range: [number, number] } | null {
  const r = new RegExp(WIKILINK_RE.source, "g");
  for (const m of text.matchAll(r)) {
    const start = m.index ?? 0;
    const end = start + m[0].length;
    if (pos >= start && pos <= end) {
      return { target: m[1].trim(), range: [start, end] };
    }
  }
  return null;
}

function wikilinkClickHandler(onNavigate: (target: string) => void): Extension {
  return EditorView.domEventHandlers({
    mousedown(event, view) {
      if (!(event.metaKey || event.ctrlKey)) return false;
      const pos = view.posAtCoords({ x: event.clientX, y: event.clientY });
      if (pos === null) return false;
      const line = view.state.doc.lineAt(pos);
      const within = findWikilinkAtPos(line.text, pos - line.from);
      if (!within) return false;
      event.preventDefault();
      onNavigate(within.target);
      return true;
    },
  });
}

export function wikilinkExtensions(onNavigate: (target: string) => void): Extension[] {
  return [
    wikilinkDecorationPlugin,
    wikilinkCompletion(),
    wikilinkClickHandler(onNavigate),
  ];
}
